<template>
  <div
    class="m-card"
    :class="{ 'm-card--light': cardImageIsLight }"
    :style="{ 'background-image': 'url(' + cardImageUrl + ')' }"
  >
    <div class="m-card__overlay" :class="{ 'is-open': isOverlayOpen }">
      <div class="m-name-plate">
        <div class="m-name-plate__wrapper">
          <a class="m-name-plate__link" :href="profileUrl" target="_blank">
            <h2 class="m-name-plate__title">
              {{ card.personal.full_name }}
              <span
                class="m-name-plate__verified"
                v-if="card.personal.is_verified"
                title="Verified profile"
              ></span>
            </h2>
          </a>
          <p class="m-name-plate__location">
            {{ card.personal.location }}
          </p>
        </div>

        <span class="m-name-plate__menu-icon">
          <span
            class="m-card-icon m-card-icon--menu"
            @click="showOverlay()"
          ></span>
        </span>

        <span class="m-name-plate__goto-icon">
          <a
            class="m-card-icon m-card-icon--goto"
            :href="profileUrl"
            target="_blank"
          ></a>
        </span>
      </div>

      <div class="m-card__contents">
        <section
          class="m-card__section"
          :class="{ 'is-active': activeSection === 'personal-info' }"
        >
          <h2 hidden>சுயவிவரம்</h2>

          <div class="m-card-field">
            <span class="m-card-field__label">{{ card.personal.caste }}</span>
            <span class="m-card-field__value">{{
              card.personal.subcaste
            }}</span>
          </div>

          <div class="m-card-field">
            <span class="m-card-field__label">திருமண நிலை</span>
            <span class="m-card-field__value">{{
              card.personal.marital_status
            }}</span>
          </div>

          <div class="m-card-field">
            <span class="m-card-field__label">வயது</span>
            <span class="m-card-field__value">{{ card.personal.age }}</span>
          </div>

          <div class="m-card-field">
            <span class="m-card-field__label">உயரம்</span>
            <span class="m-card-field__value">
              {{ card.personal.height_in_cm }}cm
            </span>
          </div>
        </section>

        <section
          class="m-card__section"
          :class="{ 'is-active': activeSection === 'profession' }"
        >
          <h2 hidden>தொழில்</h2>
          <div class="m-card-field">
            <div class="m-card-field__label">தொழில்</div>
            <div class="m-card-field__value">
              {{ card.professional.profession }}
            </div>
          </div>

          <div class="m-card-field">
            <div class="m-card-field__label">வேலை இடம்</div>
            <div class="m-card-field__value">
              {{ card.professional.job_location }}
            </div>
          </div>

          <div class="m-card-field" v-if="card.personal.education">
            <div class="m-card-field__label">கல்வி</div>
            <div class="m-card-field__value">
              {{ card.personal.education }}
            </div>
          </div>
        </section>

        <section
          class="m-card__section u-flex-col"
          :class="{ 'is-active': activeSection === 'birth-chart' }"
        >
          <h2 hidden>ஜாதகம்</h2>

          <div class="m-card__chart-selector">
            <tab-group
              :tabs="astroChartTabs"
              :ipName="`card_${card.id}_tab_group`"
              v-model="activeChartKey"
              @input="setActiveChartKey"
            />
          </div>

          <astro-chart :chart="activeChart" :label="activeChartLabel" />
        </section>

        <section
          class="m-card__section"
          :class="{ 'is-active': activeSection === 'phone' }"
        >
          <h2 hidden>தொடர்பு</h2>
          <div class="m-card-field">
            <div class="m-card-field__label">
              இவருடைய தொடர்பு தகவல்களை SMS மூலம் பெறுங்கள்.
            </div>
            <div class="m-card-field__value">
              <input
                ref="requestorPhone"
                class="m-card__ip-text"
                type="text"
                name="requestor_phone"
                placeholder="Registered mobile number"
                enterkeyhint="send"
                @keyup.enter="submitContactRequest"
              />

              <button
                ref="contactRequestBtn"
                type="submit"
                class="m-btn m-btn--secondary"
                @click="submitContactRequest"
              >
                <span class="m-btn__label">அனுப்பு</span>
                <span
                  class="m-icon-24 m-icon-24--dark m-icon-24--arrow-right"
                ></span>
              </button>
            </div>
          </div>

          <div class="m-card__btn-wrapper">
            <button
              class="m-btn"
              ref="profile_link"
              :data-clipboard-text="profileAbsoluteUrl"
            >
              <span class="m-btn-icon m-btn-icon--copy"></span>
              <span class="m-btn__label">Copy link</span>
            </button>

            <button
              class="m-btn"
              ref="share_profile"
              @click="shareProfile()"
              v-show="shareAPIEnabled"
            >
              <span class="m-btn-icon m-btn-icon--share"></span>
              <span class="m-btn__label">Share</span>
            </button>
          </div>
        </section>
      </div>
    </div>

    <div class="m-card__menu">
      <ul class="m-card-menu">
        <li
          class="m-card-menu__item m-card-icon m-card-icon--personal-info"
          :class="{ 'is-active': activeSection === 'personal-info' }"
          title="சுயவிவரம்"
          @click="changeSection('personal-info')"
        ></li>
        <li
          class="m-card-menu__item m-card-icon m-card-icon--profession"
          :class="{ 'is-active': activeSection === 'profession' }"
          title="தொழில்"
          @click="changeSection('profession')"
        ></li>
        <li
          class="m-card-menu__item m-card-icon m-card-icon--birth-chart"
          :class="{ 'is-active': activeSection === 'birth-chart' }"
          title="ஜாதகம்"
          @click="changeSection('birth-chart')"
        ></li>
        <li
          class="m-card-menu__item m-card-icon m-card-icon--phone"
          :class="{ 'is-active': activeSection === 'phone' }"
          title="தொடர்பு"
          @click="changeSection('phone')"
        ></li>
        <li
          class="m-card-menu__item m-card-icon m-card-icon--close"
          title="வெளியேறு"
          @click="hideOverlay()"
        ></li>
        <li class="m-card-menu__indicator"></li>
      </ul>
    </div>

    <ul class="m-card__tags-list">
      <li v-for="tag in card.tags" :key="tag.name">
        <router-link
          :to="getTagUrl(tag.slug, card.personal.gender)"
          class="m-tag m-tag--link"
          :class="'m-tag--' + tag.slug"
        >
          {{ tag.name }}
        </router-link>
      </li>
    </ul>
  </div>
</template>

<script>
import axios from "axios";
import ClipboardJS from "clipboard";
import AstroChart from "./AstroChart.vue";
import TabGroup from "./TabGroup.vue";

export default {
  name: "Card",
  components: {
    AstroChart,
    TabGroup,
  },
  props: {
    card: Object,
    cardImage: String,
  },
  data() {
    return {
      dCard: this.card,
      astroChartTabs: [
        {
          label: "இராசி",
          value: "raasi_chart",
        },
        {
          label: "அம்சம்",
          value: "amsam_chart",
        },
      ],
      cardImageIsLight: false,
      isOverlayOpen: false,
      isPhoneHidden: true,
      activeSection: "personal-info",
      activeChartKey: "raasi_chart",
    };
  },
  computed: {
    cardImageUrl() {
      return this.cardImage
        ? this.cardImage
        : "/static/images/km-placeholder-card.webp";
    },
    profileUrl() {
      if (process.env.NODE_ENV && process.env.NODE_ENV === "development") {
        return `http://${location.hostname}:8000/view/${this.card.id}/`;
      }

      return `/view/${this.card.id}/`;
    },
    profileAbsoluteUrl() {
      if (process.env.NODE_ENV && process.env.NODE_ENV === "development") {
        return `http://${location.hostname}:8000/view/${this.card.id}/`;
      }

      return `https://${location.hostname}/view/${this.card.id}/`;
    },
    emailUsername() {
      if (!this.card.contact.email) {
        return "";
      }

      return this.card.contact.email.split("@")[0];
    },
    emailDomain() {
      if (!this.card.contact.email) {
        return "";
      }

      return this.card.contact.email.split("@")[1];
    },
    activeChart() {
      return this.dCard.astrology[this.activeChartKey];
    },
    activeChartLabel() {
      if (this.activeChartKey === "amsam_chart") {
        return "அம்சம்";
      }

      if (this.activeChartKey === "raasi_chart") {
        return "இராசி";
      }

      return "Birth Chart";
    },
    shareAPIEnabled() {
      if (!navigator.share) {
        return false;
      }

      return true;
    },
  },
  methods: {
    showOverlay() {
      this.isOverlayOpen = true;
    },
    hideOverlay() {
      this.isOverlayOpen = false;
    },
    revealPhone() {
      if (this.$gtag) {
        this.$gtag.event("reveal_phone", {
          event_category: "Contact",
          event_label: "Profile Card",
          value: this.card.id,
        });
      }

      this.isPhoneHidden = false;
    },
    setActiveChartKey(value) {
      this.activeChartKey = value;
    },
    /**
     * detects the lightness/darkness of an image on a scale of 0 to 255
     * reference: https://stackoverflow.com/a/13763063
     */
    getImageLightness(imageSrc) {
      return new Promise((resolve, reject) => {
        if (!imageSrc) {
          return reject("Image source not defined");
        }

        var img = document.createElement("img");
        img.src = imageSrc;
        img.style.display = "none";
        document.body.appendChild(img);

        var colorSum = 0;

        img.onload = function () {
          // create canvas
          var canvas = document.createElement("canvas");
          canvas.width = this.width;
          canvas.height = this.height;

          var ctx = canvas.getContext("2d");
          ctx.drawImage(this, 0, 0);

          var imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          var data = imageData.data;
          var r, g, b, avg;

          for (var x = 0, len = data.length; x < len; x += 4) {
            r = data[x];
            g = data[x + 1];
            b = data[x + 2];

            avg = Math.floor((r + g + b) / 3);
            colorSum += avg;
          }

          var brightness = Math.floor(colorSum / (this.width * this.height));
          resolve(brightness);
        };

        img.onerror = function (err) {
          reject(err);
        };
      });
    },
    changeSection(section) {
      this.activeSection = section;
    },
    getActiveChart() {},
    shareProfile() {
      if (!navigator.share) {
        console.log("Web Sharing API not supported");
        return;
      }

      navigator
        .share({
          title: "Share profile",
          text: `Checkout ${this.card.personal.full_name}'s profile on KanchiMatrimony.com`,
          url: `https://${location.hostname}/view/${this.card.id}/`,
        })
        .then(() => console.log("Successful share"))
        .error((err) => console.error(err));
    },
    getTagUrl(tagSlug, gender) {
      const query = this.$route.query;

      delete query.page;

      return {
        name: "filter-by-tag",
        params: {
          tag: tagSlug,
          gender: gender == "ஆண்" ? "male" : null,
        },
        query: query,
      };
    },
    submitContactRequest() {
      const input = this.$refs.requestorPhone;
      const btn = this.$refs.contactRequestBtn;

      const phonePattern = new RegExp("^\\d{10}$");

      if (!phonePattern.test(input.value)) {
        alert("சரியான தொலைபேசி எண்ணை உள்ளீடு செய்யவும்.");
        return;
      }

      const data = {
        profile: this.card.id,
        requestor_phone: input.value,
      };

      const config = {
        headers: {
          "X-CSRFToken": window.getCsrfToken(),
        },
      };

      btn.disabled = true;
      axios
        .post("/api/crm/request-contact/", data, config)
        .then((response) => {
          input.value = "";

          if (response.data.requestor_profile) {
            // registered customer
            alert(
              "அன்பார்ந்த வாடிக்கையாளரே,\n\nஎங்களுடைய அலுவலக மேலாளர்" +
                " உங்களை SMS-ல் தொடர்பு கொண்டு தகவல்களை பகிர்வார். காத்திருந்தமைக்கு நன்றி."
            );
            return;
          }

          // unknown person
          alert(
            "அன்பார்ந்த வாடிக்கையாளரே, வெறும் ₹500 கட்டணம் செலுத்தி 6 மாதத்திற்கு" +
              " 25 தொடர்பு தகவல்களை வேண்டிய நேரத்தில் பெறுங்கள்."
          );
        })
        .catch(() => {
          input.value = "";

          // all errors including duplicate requests
          alert(
            "இவருடைய தொடர்பு தகவல்கள் உங்களை வந்தடையவில்லையா?\n\n" +
              "எங்களுடைய அலுவலக அலைபேசி எண் 6369-286741-ஐ SMS அல்லது WhatsApp மூலம் தொடர்பு கொள்ளவும்."
          );
        })
        .finally(() => {
          btn.disabled = false;
        });
    },
  },
  mounted() {
    this.getImageLightness(this.cardImage)
      .then((brightness) => {
        this.cardImageIsLight = brightness > 150;
      })
      .catch((err) => {
        console.log(err);
      });

    // click to copy
    const clipboard = new ClipboardJS(this.$refs.profile_link);
    clipboard.on("success", function (ev) {
      const label = ev.trigger.querySelector(".m-btn__label");
      const originalText = label.textContent;
      label.textContent = "Copied";

      setTimeout(function () {
        label.textContent = originalText;
      }, 2000);
    });
  },
};
</script>

<style lang="scss">
@import "src/scss/m-card";
</style>
